import React, { useEffect, useState, useContext, useCallback } from 'react';
import './MainContent.css';
import { BASE_URL } from '../config';
import UserContext from '../UserContext';
import PlayerStatsPanel from './PlayerStatsPanel';

const ELLIPSIS = '...';
const shorten = (addr) =>
    typeof addr === 'string' && addr.length > 10
        ? `${addr.slice(0, 6)}${ELLIPSIS}${addr.slice(-4)}`
        : addr ?? '_NoName';

const safeId = () =>
    (typeof crypto !== 'undefined' && crypto?.randomUUID ? crypto.randomUUID() : String(Math.random()));

const MainContent = () => {
    // pull from userInfo inside the context
    const { userInfo } = useContext(UserContext) || {};
    const wallet_address = userInfo?.wallet_address?.toLowerCase?.() || '';
    const jwt = userInfo?.jwt;

    const [news, setNews] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [globalStats, setGlobalStats] = useState({
        totals: {},
        enemies_kills: {},
        boss_kills: {},
        last_updated: null,
        count: 0,
    });
    const [achievements, setAchievements] = useState([]); // NEW

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // tabs: "lb" (leaderboard) | "global" | "ach" (achievements)
    const [tab, setTab] = useState('lb');

    // player stats panel
    const [statsOpen, setStatsOpen] = useState(false);
    const [statsTarget, setStatsTarget] = useState(''); // wallet we�ll look up

    const onRowClick = useCallback((wallet) => {
        if (!wallet) return;
        setStatsTarget(wallet);
        setStatsOpen(true);
    }, []);

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            if (!wallet_address || !jwt) return;

            setLoading(true);
            setError('');

            const commonOpts = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            };

            try {
                // --- Fetch News ---
                const newsRes = await fetch(`${BASE_URL}/get_news`, {
                    ...commonOpts,
                    body: JSON.stringify({ address: wallet_address, jwt }),
                });
                if (!newsRes.ok) throw new Error(`News HTTP ${newsRes.status}`);
                const newsJson = await newsRes.json();

                const newsItems = Array.isArray(newsJson?.items)
                    ? newsJson.items.map((it) => ({
                        id: it.id || it.published_at || safeId(),
                        title: it.title ?? 'Untitled',
                        summary: it.body ?? '',
                        published_at: it.published_at,
                        tags: it.tags || [],
                    }))
                    : [];

                // --- Fetch Leaderboard (+ global + achievements) ---
                const lbRes = await fetch(`${BASE_URL}/get_highscore`, {
                    ...commonOpts,
                    body: JSON.stringify({ address: wallet_address, jwt }),
                });
                if (!lbRes.ok) throw new Error(`Leaderboard HTTP ${lbRes.status}`);
                const lbJson = await lbRes.json();

                const topTen = Array.isArray(lbJson?.leaderboard_page)
                    ? lbJson.leaderboard_page
                        .slice(0, 10)
                        .sort((a, b) => (a.player_rank ?? 9999) - (b.player_rank ?? 9999))
                        .map((row) => {
                            const wallet = (row.player_wallet || '').toLowerCase();
                            return {
                                rank: Number(row.player_rank) || 0,
                                playerShort: shorten(wallet) || '_NoName',
                                wallet,
                                kills: Number(row.player_score) || 0,
                                level: Number(row.player_level) || 0,
                                isYou: wallet && wallet === wallet_address,
                            };
                        })
                    : [];

                const g = lbJson?.global || {};
                const meta = lbJson?.meta || {};
                const cleanedGlobal = {
                    totals: g.totals || {},
                    enemies_kills: g.enemies_kills || {},
                    boss_kills: g.boss_kills || {},
                    last_updated: meta?.last_updated || null,
                    count: Number(meta?.count || 0),
                };

                // achievements_list is already normalized & sorted by the API
                const achList = Array.isArray(lbJson?.achievements_list) ? lbJson.achievements_list : [];

                if (!cancelled) {
                    setNews(newsItems);
                    setLeaderboard(topTen);
                    setGlobalStats(cleanedGlobal);
                    setAchievements(achList);
                }
            } catch (e) {
                if (!cancelled) {
                    setError('Failed to load main content.');
                    console.error('[MainContent] fetch error:', e);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();
        return () => {
            cancelled = true;
        };
    }, [wallet_address, jwt]);

    // helpers
    const number = (v) => (typeof v === 'number' ? v : Number(v || 0));
    const toLabel = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const sumValues = (obj) =>
        Object.values(obj || {}).reduce((acc, v) => acc + (typeof v === 'number' ? v : Number(v || 0)), 0);

    // robust fallbacks so the UI doesn't show 0 if a key is missing/renamed
    const totalEnemyKills =
        number(globalStats?.totals?.total_enemy_kills ?? globalStats?.totals?.enemy_kills) ||
        sumValues(globalStats.enemies_kills);

    const totalBossKills =
        number(globalStats?.totals?.total_boss_kills ?? globalStats?.totals?.boss_kills) ||
        sumValues(globalStats.boss_kills);

    // Total Kills == Enemy Kills as discussed; still allow server-provided totals
    const totalKillsAllPlayers =
        number(globalStats?.totals?.total_kills_all_players ?? globalStats?.totals?.total_kills) || totalEnemyKills;

    const formatUtc = (isoStr) => {
        // Expecting "...Z"; show as UTC explicitly
        if (!isoStr) return '�';
        const d = new Date(isoStr);
        // guard against invalid dates
        if (isNaN(d.getTime())) return '�';
        return d.toLocaleString(undefined, { timeZone: 'UTC' }) + ' UTC';
    };

    return (
        <div className="mainpg-content-container">
            {loading ? (
                <div className="mainpg-content-loading">Loading main content...</div>
            ) : error ? (
                <div className="mainpg-content-error">{error}</div>
            ) : (
                <div className="mainpg-content-columns">
                    {/* News Column */}
                    <div className="mainpg-content-news">
                        <div className="mainpg-content-section-title">Latest News</div>
                        {news.length === 0 ? (
                            <div className="mainpg-content-news-empty">No news yet.</div>
                        ) : (
                            news.map((item) => (
                                <div className="mainpg-content-news-item" key={item.id}>
                                    <div className="mainpg-content-news-title">{item.title}</div>
                                    {item.published_at && (
                                        <div className="mainpg-content-news-date">{new Date(item.published_at).toLocaleString()}</div>
                                    )}
                                    <div className="mainpg-content-news-summary">{item.summary}</div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Leaderboard / Global / Achievements Column */}
                    <div className="mainpg-content-leaderboard">
                        {/* Tabs */}
                        <div className="lb-tabs">
                            <button
                                className={`lb-tab ${tab === 'lb' ? 'active' : ''}`}
                                onClick={() => setTab('lb')}
                                type="button"
                            >
                                Leaderboard
                            </button>
                            <button
                                className={`lb-tab ${tab === 'global' ? 'active' : ''}`}
                                onClick={() => setTab('global')}
                                type="button"
                            >
                                Global Stats
                            </button>
                            <button
                                className={`lb-tab ${tab === 'ach' ? 'active' : ''}`}
                                onClick={() => setTab('ach')}
                                type="button"
                            >
                                Season Achievements
                            </button>
                        </div>

                        {tab === 'lb' ? (
                            <>
                                <div className="mainpg-content-section-title">Top Players</div>

                                <div className="mainpg-content-leaderboard-list">
                                    {/* Header Row */}
                                    <div className="lb-row lb-header" aria-hidden="true">
                                        <span className="lb-col lb-rank">Rank</span>
                                        <span className="lb-col lb-player">Player</span>
                                        <span className="lb-col lb-level">Level</span>
                                        <span className="lb-col lb-kills">Kills</span>
                                    </div>

                                    {/* Data Rows */}
                                    {leaderboard.length === 0 ? (
                                        <div className="mainpg-content-leaderboard-empty">No scores yet.</div>
                                    ) : (
                                        leaderboard.map((entry) => {
                                            const topClass =
                                                entry.rank === 1 ? 'top-1' : entry.rank === 2 ? 'top-2' : entry.rank === 3 ? 'top-3' : '';
                                            const youClass = entry.isYou ? 'is-you' : '';
                                            return (
                                                <div
                                                    className={`lb-row ${topClass} ${youClass}`}
                                                    key={`${entry.rank}-${entry.wallet || entry.playerShort}`}
                                                    title={`${entry.wallet} � Kills: ${entry.kills} � Level: ${entry.level}`}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => onRowClick(entry.wallet)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') onRowClick(entry.wallet);
                                                    }}
                                                >
                                                    <span className="lb-col lb-rank">
                                                        <span className="lb-medal">#{entry.rank}</span>
                                                    </span>

                                                    <span className="lb-col lb-player" data-wallet={entry.wallet}>
                                                        {entry.playerShort}
                                                        {entry.isYou && <span className="lb-you">YOU</span>}
                                                    </span>

                                                    <span className="lb-col lb-level">
                                                        <span className="lb-level-pill">Lv {entry.level}</span>
                                                    </span>

                                                    <span className="lb-col lb-kills">
                                                        <strong>{entry.kills.toLocaleString()}</strong>
                                                        <span className="lb-kills-label">&nbsp;kills</span>
                                                    </span>

                                                    {/* ambient bits */}
                                                    <span className="lb-sheen" aria-hidden="true" />
                                                    <span className="lb-rail" aria-hidden="true" />
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </>
                        ) : tab === 'global' ? (
                            <>
                                <div className="mainpg-content-section-title">Global Stats</div>

                                {/* Totals */}
                                <div className="global-grid">
                                    <div className="global-card">
                                        <div className="global-label">All Player Kills</div>
                                        <div className="global-value">{totalKillsAllPlayers.toLocaleString()}</div>
                                    </div>
                                    <div className="global-card">
                                        <div className="global-label">Total Games Played</div>
                                        <div className="global-value">
                                            {number(globalStats?.totals?.total_games_played ?? globalStats?.totals?.games_played).toLocaleString()}
                                        </div>
                                    </div>
                                    <div className="global-card">
                                        <div className="global-label">All Player Deaths</div>
                                        <div className="global-value">
                                            {number(globalStats?.totals?.total_deaths ?? globalStats?.totals?.deaths).toLocaleString()}
                                        </div>
                                    </div>

                                    <div className="global-card">
                                        <div className="global-label">Boss Kills</div>
                                        <div className="global-value">{totalBossKills.toLocaleString()}</div>
                                    </div>
                                    <div className="global-card">
                                        <div className="global-label">Proxima Fleet Size (Players) </div>
                                        <div className="global-value">{number(globalStats.count).toLocaleString()}</div>
                                    </div>
                                </div>

                                {/* Kills breakdowns */}
                                <div className="global-columns">
                                    <div className="global-table-card">
                                        <div className="global-table-title">Enemy Types (All Players)</div>
                                        <div className="psp-table">
                                            <div className="psp-thead">
                                                <div>Name</div>
                                                <div className="psp-num">Kills</div>
                                            </div>
                                            <div className="psp-tbody">
                                                {Object.keys(globalStats.enemies_kills || {}).length === 0 ? (
                                                    <div className="psp-empty">No enemy data.</div>
                                                ) : (
                                                    Object.entries(globalStats.enemies_kills)
                                                        .filter(([, v]) => number(v) > 0)
                                                        .sort((a, b) => number(b[1]) - number(a[1]))
                                                        .map(([name, val]) => (
                                                            <div className="psp-row" key={name}>
                                                                <div className="psp-name">{toLabel(name)}</div>
                                                                <div className="psp-num">{number(val).toLocaleString()}</div>
                                                            </div>
                                                        ))
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="global-table-card">
                                        <div className="global-table-title">Boss Kills (All Players)</div>
                                        <div className="psp-table">
                                            <div className="psp-thead">
                                                <div>Name</div>
                                                <div className="psp-num">Kills</div>
                                            </div>
                                            <div className="psp-tbody">
                                                {Object.keys(globalStats.boss_kills || {}).length === 0 ? (
                                                    <div className="psp-empty">No boss data.</div>
                                                ) : (
                                                    Object.entries(globalStats.boss_kills)
                                                        .filter(([, v]) => number(v) > 0)
                                                        .sort((a, b) => number(b[1]) - number(a[1]))
                                                        .map(([name, val]) => (
                                                            <div className="psp-row" key={name}>
                                                                <div className="psp-name">{toLabel(name)}</div>
                                                                <div className="psp-num">{number(val).toLocaleString()}</div>
                                                            </div>
                                                        ))
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Timestamp */}
                                <div className="global-meta">
                                    Last updated:{' '}
                                    {globalStats.last_updated ? formatUtc(globalStats.last_updated) : '�'}
                                </div>
                            </>
                        ) : (
                            // === Season Achievements ===
                            <>
                                <div className="mainpg-content-section-title">Season Achievements</div>

                                {achievements.length === 0 ? (
                                    <div className="mainpg-content-leaderboard-empty">No achievements awarded yet.</div>
                                ) : (
                                    <div className="ach-grid">
                                        {achievements.map((a) => {
                                            const isLevel = (a.type || '').toLowerCase() === 'level';
                                            const isKills = (a.type || '').toLowerCase() === 'kills';
                                            const pillText = isLevel
                                                ? `Lv ${Number(a.value || 0)}`
                                                : `${Number(a.value || 0).toLocaleString()} kills`;
                                            return (
                                                <div className="ach-card" key={a.key || `${a.type}-${a.threshold}-${a.wallet}`}>
                                                    <div className="ach-head">
                                                        <div className="ach-type">{isLevel ? 'LEVEL' : 'KILLS'}</div>
                                                        <div className="ach-pill">{pillText}</div>
                                                    </div>
                                                    <div className="ach-name">{a.name || 'Achievement'}</div>
                                                    <div className="ach-row">
                                                        <span className="ach-label">Winner</span>
                                                        <span className="ach-value">{shorten(a.wallet)}</span>
                                                    </div>
                                                    <div className="ach-row">
                                                        <span className="ach-label">Threshold</span>
                                                        <span className="ach-value">
                                                            {isLevel
                                                                ? `Level ${Number(a.threshold || 0)}`
                                                                : `${Number(a.threshold || 0).toLocaleString()} kills`}
                                                        </span>
                                                    </div>
                                                    <div className="ach-row">
                                                        <span className="ach-label">Profile Timestamp</span>
                                                        <span className="ach-value">{formatUtc(a.basis_timestamp)}</span>
                                                    </div>
                                                    <div className="ach-row">
                                                        <span className="ach-label">Awarded</span>
                                                        <span className="ach-value">{formatUtc(a.achieved_at)}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Player Stats Panel */}
            <PlayerStatsPanel
                open={statsOpen}
                onClose={() => setStatsOpen(false)}
                viewerWallet={wallet_address}
                jwt={jwt}
                targetWallet={statsTarget}
            />
        </div>
    );
};

export default MainContent;
